import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { taxpayerDetailPath } from "../../Router/constant";
import DonutChart from "../../Components/Charts/DonutChart";
import HeatMap from "../../Components/Charts/HeatMap";
import MultiTrendChart from "../../Components/Charts/MultiTrendChart";
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  ArrowRightIcon,
  ArrowUpRightIcon,
  CalendarIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  CreditCardIcon,
  DocCheckIcon,
  DownloadIcon,
  EyeIcon,
  InfoIcon,
  MoreIcon,
  NetworkIcon,
  ReportIcon,
  ShieldCheckIcon,
  UserIcon,
  UsersIcon,
} from "../../Components/Icons";
import {
  COMPLIANCE_ALERTS,
  HEAT_COLS,
  HEAT_ROWS,
  HEAT_VALUES,
  HIGH_RISK_TAXPAYERS,
  INDUSTRIES,
  RISK_DISTRIBUTION,
  RISK_FACTORS,
  RISK_STATS,
  RISK_TREND,
  TOTAL_HIGH_RISK,
  TOTAL_TAXPAYERS,
  TREND_MAX,
  TREND_MONTHS,
} from "./data";
import "./Risk.css";

const STAT_ICONS = {
  shield: ShieldCheckIcon,
  usersHigh: UsersIcon,
  usersMedium: UsersIcon,
  usersLow: UsersIcon,
  check: CheckCircleIcon,
  cases: ReportIcon,
};

const FACTOR_ICONS = {
  user: UserIcon,
  users: UsersIcon,
  doc: DocCheckIcon,
  cash: CreditCardIcon,
  network: NetworkIcon,
};

const ALERT_ICONS = {
  high: AlertTriangleIcon,
  medium: AlertTriangleIcon,
  low: AlertCircleIcon,
};

const PER_PAGE_OPTIONS = [10, 25, 50, 100];
const AXIS_LABELS = TREND_MONTHS.map((month) => month.split(" ")[0]);

const scoreTone = (score) => {
  if (score >= 80) return "good";
  if (score >= 50) return "warning";
  return "critical";
};

function CardHeader({ title, action, info }) {
  return (
    <div className="dgi-card__header">
      <h2 className="dgi-card__title">
        {title}
        {info && (
          <span className="dgi-card__info" title={`About ${title}`}>
            <InfoIcon size={15} />
          </span>
        )}
      </h2>
      {action}
    </div>
  );
}

export default function RiskCompliance() {
  const [granularity, setGranularity] = useState("Monthly");
  const [industry, setIndustry] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const rows = useMemo(
    () =>
      industry
        ? HIGH_RISK_TAXPAYERS.filter((item) => item.industry === industry)
        : HIGH_RISK_TAXPAYERS,
    [industry]
  );

  const totalPages = Math.ceil(TOTAL_HIGH_RISK / perPage);

  return (
    <div className="page">
      {/* --------------------------------------------------------- head -- */}
      <div className="page__head">
        <div>
          <h1 className="page__title">Risk &amp; Compliance</h1>
          <p className="page__subtitle">
            Monitor taxpayer risks and ensure compliance with tax regulations
            and policies.
          </p>
        </div>

        <div className="page__actions">
          <button type="button" className="dgi-btn dgi-btn--ghost">
            <CalendarIcon size={17} />
            01 Jan 2026 - 01 Sep 2026
            <ChevronDownIcon size={16} />
          </button>
          <button type="button" className="dgi-btn dgi-btn--primary">
            <DownloadIcon size={17} />
            Export Report
          </button>
        </div>
      </div>

      {/* -------------------------------------------------------- stats -- */}
      <section className="row g-3" aria-label="Risk summary">
        {RISK_STATS.map((card) => {
          const Icon = STAT_ICONS[card.icon];
          return (
            <div className="col-6 col-lg-4 col-xxl-2" key={card.key}>
              <article className="risk-stat h-100">
                <div className="risk-stat__top">
                  <span className={`kpi__icon kpi__icon--${card.tone}`}>
                    <Icon size={20} />
                  </span>
                  <div className="risk-stat__meta">
                    <p className="kpi__label">{card.label}</p>
                    <p className="risk-stat__value">
                      {card.value}
                      {card.unit && <span>{card.unit}</span>}
                    </p>
                    <p className="risk-stat__sub">{card.sub}</p>
                  </div>
                </div>

                {card.progress != null && (
                  <span className="score__track risk-stat__track">
                    <span
                      className={`score__fill score__fill--${card.progressTone}`}
                      style={{ width: `${card.progress}%` }}
                    />
                  </span>
                )}

                <p
                  className={`risk-stat__delta risk-stat__delta--${card.direction}`}
                >
                  <ArrowUpRightIcon size={13} />
                  {card.delta}
                </p>
              </article>
            </div>
          );
        })}
      </section>

      {/* ------------------------------------------------------- charts -- */}
      <div className="risk-charts">
        <div>
          <article className="dgi-card h-100">
            <CardHeader title="Risk Distribution" />
            <DonutChart
              segments={RISK_DISTRIBUTION}
              total={TOTAL_TAXPAYERS.toLocaleString("en-US")}
              totalLabel="Total Taxpayers"
            />
            <button type="button" className="dgi-card__link">
              View Risk Scoring Details <ArrowRightIcon size={16} />
            </button>
          </article>
        </div>

        <div>
          <article className="dgi-card h-100">
            <CardHeader
              title="Risk Trend Over Time"
              action={
                <label className="select">
                  <select
                    value={granularity}
                    onChange={(event) => setGranularity(event.target.value)}
                    aria-label="Trend granularity"
                  >
                    <option>Monthly</option>
                    <option>Quarterly</option>
                    <option>Yearly</option>
                  </select>
                  <ChevronDownIcon size={16} />
                </label>
              }
            />
            <MultiTrendChart
              labels={AXIS_LABELS}
              series={RISK_TREND}
              max={TREND_MAX}
              tickCount={6}
              formatTick={(value) =>
                value >= 1000
                  ? `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}K`
                  : `${value}`
              }
            />
          </article>
        </div>

        <div>
          <article className="dgi-card h-100">
            <CardHeader title="Risk Score Heat Map" info />
            <HeatMap
              rowLabels={HEAT_ROWS}
              colLabels={HEAT_COLS}
              values={HEAT_VALUES}
              rowAxis="Likelihood"
              colAxis="Impact"
            />
            <button type="button" className="dgi-card__link">
              View Risk Matrix <ArrowRightIcon size={16} />
            </button>
          </article>
        </div>
      </div>

      {/* -------------------------------------------- table + side lists -- */}
      <div className="risk-lower">
        <section className="dgi-card">
          <CardHeader
            title="High Risk Taxpayers"
            info
            action={
              <div className="risk-table__actions">
                <span className="select">
                  <select
                    value={industry}
                    aria-label="Filter by industry"
                    onChange={(event) => {
                      setIndustry(event.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All Industries</option>
                    {INDUSTRIES.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon size={16} />
                </span>
                <button
                  type="button"
                  className="dgi-card__link dgi-card__link--inline"
                >
                  View All <ArrowRightIcon size={16} />
                </button>
              </div>
            }
          />

          <div className="dgi-table-wrap">
            <table className="dgi-table dgi-table--risk">
              <thead>
                <tr>
                  <th>Taxpayer Name</th>
                  <th>NIF</th>
                  <th>Industry</th>
                  <th>Risk Score</th>
                  <th>Risk Level</th>
                  <th>Key Risk Factor</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <tr key={item.nif}>
                    <td>
                      <Link className="link" to={taxpayerDetailPath(item.nif)}>
                        {item.name}
                      </Link>
                    </td>
                    <td>{item.nif}</td>
                    <td>{item.industry}</td>
                    <td>
                      <span className="score">
                        <span className="score__value">
                          <strong>{item.score}</strong> / 100
                        </span>
                        <span className="score__track">
                          <span
                            className={`score__fill score__fill--${scoreTone(
                              item.score
                            )}`}
                            style={{ width: `${item.score}%` }}
                          />
                        </span>
                      </span>
                    </td>
                    <td>
                      <span className={`chip chip--${item.level.toLowerCase()}`}>
                        {item.level}
                      </span>
                    </td>
                    <td className="risk-factor-cell">{item.factor}</td>
                    <td>
                      {item.updatedDate}
                      <span className="dgi-table__sub">{item.updatedTime}</span>
                    </td>
                    <td>
                      <span className="row-actions">
                        <Link
                          className="row-actions__btn"
                          to={taxpayerDetailPath(item.nif)}
                          aria-label={`View ${item.name}`}
                        >
                          <EyeIcon size={17} />
                        </Link>
                        <button
                          type="button"
                          className="dgi-card__more"
                          aria-label={`Actions for ${item.name}`}
                        >
                          <MoreIcon size={18} />
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rows.length === 0 && (
            <p className="dgi-table__empty">
              No high risk taxpayers in this industry.
            </p>
          )}

          <div className="listbar listbar--foot">
            <p className="listbar__count">
              Showing {rows.length === 0 ? 0 : 1} to {rows.length} of{" "}
              {TOTAL_HIGH_RISK.toLocaleString("en-US")} high risk taxpayers
            </p>

            <div className="listbar__pager">
              <span className="select">
                <select
                  value={perPage}
                  aria-label="Rows per page"
                  onChange={(event) => {
                    setPerPage(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  {PER_PAGE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option} per page
                    </option>
                  ))}
                </select>
                <ChevronDownIcon size={16} />
              </span>

              <nav className="pager" aria-label="Pagination">
                <button
                  type="button"
                  className="pager__btn"
                  aria-label="First page"
                  disabled={page === 1}
                  onClick={() => setPage(1)}
                >
                  <ChevronsLeftIcon size={16} />
                </button>
                <button
                  type="button"
                  className="pager__btn"
                  aria-label="Previous page"
                  disabled={page === 1}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                >
                  <ChevronLeftIcon size={16} />
                </button>

                {[1, 2, 3, 4, 5].map((entry) => (
                  <button
                    key={entry}
                    type="button"
                    className={`pager__btn${
                      entry === page ? " pager__btn--active" : ""
                    }`}
                    aria-current={entry === page ? "page" : undefined}
                    onClick={() => setPage(entry)}
                  >
                    {entry}
                  </button>
                ))}
                <span className="pager__gap">…</span>
                <button
                  type="button"
                  className="pager__btn"
                  onClick={() => setPage(totalPages)}
                >
                  {totalPages.toLocaleString("en-US")}
                </button>

                <button
                  type="button"
                  className="pager__btn"
                  aria-label="Next page"
                  disabled={page === totalPages}
                  onClick={() =>
                    setPage((prev) => Math.min(prev + 1, totalPages))
                  }
                >
                  <ChevronRightIcon size={16} />
                </button>
                <button
                  type="button"
                  className="pager__btn"
                  aria-label="Last page"
                  disabled={page === totalPages}
                  onClick={() => setPage(totalPages)}
                >
                  <ChevronsRightIcon size={16} />
                </button>
              </nav>
            </div>
          </div>
        </section>

        <div className="stack">
          <section className="dgi-card">
            <CardHeader
              title="Top Risk Factors"
              info
              action={
                <button
                  type="button"
                  className="dgi-card__link dgi-card__link--inline"
                >
                  View All <ArrowRightIcon size={16} />
                </button>
              }
            />
            <ul className="factors">
              {RISK_FACTORS.map((factor) => {
                const Icon = FACTOR_ICONS[factor.icon];
                return (
                  <li className="factors__row" key={factor.label}>
                    <Icon size={18} />
                    <span className="factors__label">{factor.label}</span>
                    <span className="factors__count">{factor.count}</span>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="dgi-card">
            <CardHeader
              title="Recent Compliance Alerts"
              action={
                <button
                  type="button"
                  className="dgi-card__link dgi-card__link--inline"
                >
                  View All <ArrowRightIcon size={16} />
                </button>
              }
            />
            <ul className="ralerts">
              {COMPLIANCE_ALERTS.map((alert) => {
                const Icon = ALERT_ICONS[alert.severity];
                return (
                  <li className="ralert" key={alert.id}>
                    <span className={`ralert__icon ralert__icon--${alert.severity}`}>
                      <Icon size={18} />
                    </span>
                    <div>
                      <p className="ralert__title">{alert.title}</p>
                      <p className="ralert__who">{alert.who}</p>
                      <p className="ralert__when">{alert.when}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
            <button type="button" className="dgi-card__link">
              Go to Compliance Monitoring <ArrowRightIcon size={16} />
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}
